// Level Entity
// Represents static entities loaded from level data (enemies, powerups, etc.)
// These are visual/interactive objects with rotation, scale, and color

import { Entity } from '../core/Entity';
import { VectorRenderer } from '../graphics/VectorRenderer';
import { VectorShape } from '../utils/types';

// Define thematic shapes for each enemy type
const ENEMY_SHAPES: Record<string, VectorShape> = {
  // Defensive turret with gun barrel
  sentryNode: {
    points: [
      { x: 0, y: -15 },
      { x: -3, y: -10 },
      { x: -8, y: -10 },
      { x: -8, y: 8 },
      { x: 8, y: 8 },
      { x: 8, y: -10 },
      { x: 3, y: -10 },
    ],
    closed: true,
  },
  // Radiating tower with emanating energy spikes
  pulseTower: {
    points: [
      { x: 0, y: -14 },
      { x: -5, y: -8 },
      { x: -10, y: -10 },
      { x: -8, y: -4 },
      { x: -12, y: 0 },
      { x: -8, y: 4 },
      { x: -10, y: 10 },
      { x: -5, y: 8 },
      { x: 0, y: 14 },
      { x: 5, y: 8 },
      { x: 10, y: 10 },
      { x: 8, y: 4 },
      { x: 12, y: 0 },
      { x: 8, y: -4 },
      { x: 10, y: -10 },
      { x: 5, y: -8 },
    ],
    closed: true,
  },
  // Cross/shield shape with protective curves
  shieldGenerator: {
    points: [
      { x: 0, y: -14 },
      { x: -4, y: -10 },
      { x: -10, y: -4 },
      { x: -14, y: 0 },
      { x: -10, y: 4 },
      { x: -4, y: 10 },
      { x: 0, y: 14 },
      { x: 4, y: 10 },
      { x: 10, y: 4 },
      { x: 14, y: 0 },
      { x: 10, y: -4 },
      { x: 4, y: -10 },
    ],
    closed: true,
  },
  // Complex star - important mission target
  conversionNode: {
    points: [
      { x: 0, y: -18 },
      { x: -5, y: -8 },
      { x: -15, y: -10 },
      { x: -8, y: -3 },
      { x: -18, y: 0 },
      { x: -8, y: 3 },
      { x: -15, y: 10 },
      { x: -5, y: 8 },
      { x: 0, y: 18 },
      { x: 5, y: 8 },
      { x: 15, y: 10 },
      { x: 8, y: 3 },
      { x: 18, y: 0 },
      { x: 8, y: -3 },
      { x: 15, y: -10 },
      { x: 5, y: -8 },
    ],
    closed: true,
  },
  // Arrow/dart - aggressive and fast
  seekerDrone: {
    points: [
      { x: 0, y: -10 },
      { x: -3, y: -5 },
      { x: -2, y: 0 },
      { x: -4, y: 6 },
      { x: -2, y: 8 },
      { x: 0, y: 6 },
      { x: 2, y: 8 },
      { x: 4, y: 6 },
      { x: 2, y: 0 },
      { x: 3, y: -5 },
    ],
    closed: true,
  },
  // Energy sphere with radiating spikes
  resonanceSphere: {
    points: [
      { x: 0, y: -14 },
      { x: 3, y: -13 },
      { x: 7, y: -12 },
      { x: 10, y: -10 },
      { x: 12, y: -7 },
      { x: 13, y: -3 },
      { x: 14, y: 0 },
      { x: 13, y: 3 },
      { x: 12, y: 7 },
      { x: 10, y: 10 },
      { x: 7, y: 12 },
      { x: 3, y: 13 },
      { x: 0, y: 14 },
      { x: -3, y: 13 },
      { x: -7, y: 12 },
      { x: -10, y: 10 },
      { x: -12, y: 7 },
      { x: -13, y: 3 },
      { x: -14, y: 0 },
      { x: -13, y: -3 },
      { x: -12, y: -7 },
      { x: -10, y: -10 },
      { x: -7, y: -12 },
      { x: -3, y: -13 },
    ],
    closed: true,
  },
};

// Define thematic shapes for powerups
const POWERUP_SHAPES: Record<string, VectorShape> = {
  fuelPod: {
    points: [
      { x: -5, y: -8 },
      { x: 5, y: -8 },
      { x: 7, y: -5 },
      { x: 7, y: 5 },
      { x: 5, y: 8 },
      { x: -5, y: 8 },
      { x: -7, y: 5 },
      { x: -7, y: -5 },
    ],
    closed: true,
  },
  fuelTank: {
    points: [
      { x: -7, y: -11 },
      { x: 7, y: -11 },
      { x: 10, y: -7 },
      { x: 10, y: 7 },
      { x: 7, y: 11 },
      { x: -7, y: 11 },
      { x: -10, y: 7 },
      { x: -10, y: -7 },
    ],
    closed: true,
  },
  shieldCrystal: {
    points: [
      { x: 0, y: -10 },
      { x: -6, y: -4 },
      { x: -7, y: 4 },
      { x: 0, y: 10 },
      { x: 7, y: 4 },
      { x: 6, y: -4 },
    ],
    closed: true,
  },
  shieldCore: {
    points: [
      { x: 0, y: -14 },
      { x: -8, y: -5 },
      { x: -10, y: 5 },
      { x: 0, y: 14 },
      { x: 10, y: 5 },
      { x: 8, y: -5 },
    ],
    closed: true,
  },
  smartBomb: {
    points: [
      { x: 0, y: -10 },
      { x: -3, y: -3 },
      { x: -10, y: 0 },
      { x: -3, y: 3 },
      { x: 0, y: 10 },
      { x: 3, y: 3 },
      { x: 10, y: 0 },
      { x: 3, y: -3 },
    ],
    closed: true,
  },
  pointStar: {
    points: [
      { x: 0, y: -12 },
      { x: -3, y: -4 },
      { x: -11, y: -4 },
      { x: -5, y: 2 },
      { x: -7, y: 10 },
      { x: 0, y: 5 },
      { x: 7, y: 10 },
      { x: 5, y: 2 },
      { x: 11, y: -4 },
      { x: 3, y: -4 },
    ],
    closed: true,
  },
  extraLife: {
    points: [
      { x: -6, y: -6 },
      { x: -9, y: -3 },
      { x: -9, y: 0 },
      { x: -6, y: 3 },
      { x: 0, y: 8 },
      { x: 6, y: 3 },
      { x: 9, y: 0 },
      { x: 9, y: -3 },
      { x: 6, y: -6 },
      { x: 3, y: -6 },
      { x: 0, y: -3 },
      { x: -3, y: -6 },
    ],
    closed: true,
  },
  weaponBoost: {
    points: [
      { x: 3, y: -10 },
      { x: -2, y: -2 },
      { x: 4, y: -2 },
      { x: -3, y: 10 },
      { x: 2, y: 2 },
      { x: -4, y: 2 },
    ],
    closed: true,
  },
};

// Default powerup shape (hexagon)
const POWERUP_SHAPE: VectorShape = {
  points: [
    { x: 0, y: -10 },
    { x: -8, y: -5 },
    { x: -8, y: 5 },
    { x: 0, y: 10 },
    { x: 8, y: 5 },
    { x: 8, y: -5 },
  ],
  closed: true,
};

export class LevelEntity extends Entity {
  private entityType: 'enemy' | 'powerup';
  private subType: string;  // e.g., 'sentryNode', 'fuelPod'

  constructor(
    x: number,
    y: number,
    color: string,
    entityType: 'enemy' | 'powerup',
    subType: string,
    shape?: [number, number][]
  ) {
    super(x, y);

    this.entityType = entityType;
    this.subType = subType;
    this.color = color;

    // Use provided shape or default based on type
    if (shape && shape.length > 0) {
      this.shape = {
        points: shape.map(([x, y]) => ({ x, y })),
        closed: true
      };
      console.log(`LevelEntity ${subType} using custom shape with ${shape.length} vertices`);
    } else if (entityType === 'enemy' && ENEMY_SHAPES[subType]) {
      this.shape = ENEMY_SHAPES[subType];
      console.log(`LevelEntity ${subType} using default enemy shape`);
    } else if (entityType === 'powerup' && POWERUP_SHAPES[subType]) {
      this.shape = POWERUP_SHAPES[subType];
      console.log(`LevelEntity ${subType} using default powerup shape for ${subType}`);
    } else {
      this.shape = POWERUP_SHAPE;
      console.log(`LevelEntity ${subType} using fallback powerup shape`);
    }
  }

  update(_dt: number): void {
    // Static entities don't update (yet)
    // Could add effects like rotation, pulsing, etc. later
  }

  render(renderer: VectorRenderer): void {
    if (!this.active) return;

    // Add glow effect
    const glowSize = this.entityType === 'enemy' ? 6 : 4;
    renderer.setGlow(glowSize, this.color);

    // Render with inherited rotation, scale, color from Entity base class
    super.render(renderer);

    renderer.clearGlow();
  }

  getEntityType(): 'enemy' | 'powerup' {
    return this.entityType;
  }

  getSubType(): string {
    return this.subType;
  }
}
